import React, { useEffect } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Switch, Button, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { AppVersionItem } from '../graphql/queries/getAppVersionList';
import dayjs from 'dayjs';

const { TextArea } = Input;

interface AppVersionFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: any) => void;
    appVersion: AppVersionItem | null;
    mode: 'create' | 'edit';
    loading?: boolean;
}

const APP_TYPE_OPTIONS = [
    { label: 'PDA', value: 'pda' },
    { label: 'Android', value: 'mobile_android' },
    { label: 'iOS', value: 'mobile_ios' },
];

const AppVersionFormDrawer: React.FC<AppVersionFormDrawerProps> = ({
    visible, onClose, onSubmit, appVersion, mode, loading
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            if (mode === 'edit' && appVersion) {
                form.setFieldsValue({
                    type: appVersion.type,
                    versionCode: appVersion.versionCode,
                    versionName: appVersion.versionName,
                    url: appVersion.url,
                    isForceUpdate: appVersion.isForceUpdate,
                    forceUpdateVersionCode: appVersion.forceUpdateVersionCode,
                    enabledAt: appVersion.enabledAt ? dayjs(appVersion.enabledAt) : null,
                    description: appVersion.description,
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, mode, appVersion, form]);

    const handleFinish = (values: any) => {
        const submitValues = {
            ...values,
            enabledAt: values.enabledAt ? values.enabledAt.toISOString() : null,
            isForceUpdate: values.isForceUpdate || false,
            forceUpdateVersionCode: values.forceUpdateVersionCode || null,
        };
        if (mode === 'edit' && appVersion) {
            submitValues.id = appVersion.id;
        }
        onSubmit(submitValues);
    };

    return (
        <Drawer
            title={mode === 'create'
                ? t('central.appVersion.drawer.createTitle')
                : t('central.appVersion.drawer.editTitle')
            }
            placement="right"
            width={500}
            onClose={onClose}
            open={visible}
            extra={
                <Button type="primary" onClick={() => form.submit()} loading={loading}>
                    <span className="font-bold">{t('components.button.save')}</span>
                </Button>
            }
        >
            <Form
                form={form}
                layout="vertical"
                labelAlign="left"
                onFinish={handleFinish}
            >
                <Form.Item
                    name="type"
                    label={t('central.appVersion.columns.type')}
                    rules={[{ required: true, message: t('central.appVersion.form.typeRequired') as string }]}
                >
                    <Select
                        size="middle"
                        options={APP_TYPE_OPTIONS}
                        placeholder={t('central.appVersion.form.typePlaceholder')}
                    />
                </Form.Item>

                <Form.Item
                    name="versionCode"
                    label={t('central.appVersion.columns.versionCode')}
                    rules={[{ required: true, message: t('central.appVersion.form.versionCodeRequired') as string }]}
                >
                    <InputNumber size="middle" min={1} style={{ width: '100%' }} placeholder="80700"/>
                </Form.Item>

                <Form.Item
                    name="versionName"
                    label={t('central.appVersion.columns.versionName')}
                    rules={[{ required: true, message: t('central.appVersion.form.versionNameRequired') as string }]}
                >
                    <Input size="middle" placeholder="V8.7.0" />
                </Form.Item>

                <Form.Item
                    name="url"
                    label={t('central.appVersion.columns.url')}
                    rules={[{ required: true, message: t('central.appVersion.form.urlRequired') as string }]}
                >
                    <Input size="middle" placeholder="https://" />
                </Form.Item>

                <Form.Item
                    name="isForceUpdate"
                    label={t('central.appVersion.columns.isForceUpdate')}
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    name="forceUpdateVersionCode"
                    label={t('central.appVersion.columns.forceUpdateVersionCode')}
                >
                    <InputNumber size="middle" min={1} style={{ width: '100%' }} placeholder="80700"/>
                </Form.Item>

                <Form.Item
                    name="enabledAt"
                    label={t('central.appVersion.columns.enabledAt')}
                >
                    <DatePicker
                        size="middle"
                        showTime
                        style={{ width: '100%' }}
                    />
                </Form.Item>

                <Form.Item
                    name="description"
                    label={t('central.appVersion.columns.description')}
                >
                    <TextArea
                        rows={6}
                        placeholder={t('central.appVersion.form.descriptionPlaceholder') as string}
                    />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default AppVersionFormDrawer;
